'use client';

import React, { Children, ReactNode, useState } from 'react';

import { cn } from '@kit/ui/utils';

import Background from '~/components/marketing/sections/new-hero/background';
import GlowingBadge from '~/components/marketing/ui/glowing-badge';

export type UpdateEntry = {
  heading: string;
  content: ReactNode;
  date: string | null;
  video: string | null;
};

function ContentWithStyles({ children }: { children: ReactNode }) {
  const list = Children.toArray(children);

  if (list.length > 1)
    return Children.map(children, (child) => (
      <ContentWithStyles>{child}</ContentWithStyles>
    ));

  const child = list[0];

  if (!child || typeof child !== 'object' || !('props' in child)) return child;

  const element = child as React.JSX.Element;

  if (element.type === 'img') {
    return (
      <div className="mt-6 flex w-full justify-center">
        {React.cloneElement(element, { className: 'w-full rounded-[30px]' })}
      </div>
    );
  } else if (element.type === 'p') {
    if (element.props.children?.type === 'img')
      return <ContentWithStyles>{element.props.children}</ContentWithStyles>;

    return React.cloneElement(element, {
      className:
        'mb-4 mt-4 text-left text-base font-normal leading-6 text-gray-300',
    });
  } else if (element.type === 'h2') {
    return React.cloneElement(element, {
      className: 'mt-6 text-xl font-semibold',
    });
  } else if (element.type === 'ul') {
    return React.cloneElement(element, {
      className: 'mt-8 list-disc text-gray-300 max-w-[90%] mx-auto',
      children: <ContentWithStyles>{element.props.children}</ContentWithStyles>,
    });
  } else if (element.type === 'ol') {
    return React.cloneElement(element, {
      className:
        'mt-8 list-decimal text-gray-300 text-justify max-w-[90%] mx-auto',
      children: <ContentWithStyles>{element.props.children}</ContentWithStyles>,
    });
  } else if (element.type === 'li') {
    return React.cloneElement(element, {
      className: 'mb-4',
    });
  } else if (element.type === 'blockquote') {
    return React.cloneElement(element, {
      className: 'pl-8 border-l8 border-l-4 border-gray-600 z',
    });
  } else if (element.type === 'hr') {
    return React.cloneElement(element, {
      className: 'my-8',
    });
  }

  if (!('children' in (child.props as unknown as object))) return child;

  return React.cloneElement(element, {
    children: <ContentWithStyles>{element.props.children}</ContentWithStyles>,
  });
}

export function UpdatesContainer({
  updates,
}: {
  updates: { slug: string; entry: UpdateEntry }[];
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectedEntry = updates[selectedIndex]?.entry;

  return (
    <>
      <div className="absolute top-0 z-0 mx-auto flex h-screen w-full flex-col items-center justify-start overflow-hidden px-6 pt-[137px] lg:px-8 lg:pt-36">
        <div
          className="pointer-events-none absolute bottom-0 left-0 z-40 h-[30%] w-full lg:h-[16%]"
          style={{
            background:
              'linear-gradient(180deg, rgba(5, 5, 10, 0.00) 0%, #05050A 100%)',
          }}
        ></div>
        <Background showLights={false} />
      </div>

      <div className="relative overflow-hidden px-4">
        <div className="mx-auto flex w-full max-w-7xl items-start pb-60 text-white max-lg:flex-col-reverse">
          {/* Sidebar */}
          <div className="max-h sticky top-40 w-56 shrink-0">
            <ul className="min-h-[80px] space-y-[26px] [mask-image:linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_100%)]">
              {updates.map(({ entry: { date } }, index) => (
                <li
                  key={index}
                  className={cn(
                    'flex cursor-pointer items-center space-x-4 text-white transition-all duration-300',
                    { 'opacity-50': selectedIndex !== index },
                  )}
                  onClick={() => setSelectedIndex(index)}
                >
                  {index === selectedIndex ? (
                    <>
                      <span className="h-px w-5 bg-white/50"></span>
                      <span className="whitespace-nowrap">{date}</span>
                      <span className="h-px w-5 bg-white/50"></span>
                    </>
                  ) : (
                    <span className="col-span-3">{date}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Main Content */}
          <div className="mx-[clamp(24px,_calc((100vw_-_735px)_/_3.3),_150px)] flex-1 pb-16 max-lg:mx-0">
            <GlowingBadge innerClassName="px-2.5 py-1.5" className="mb-4">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2.33331 7.58333L5.24998 1.75H10.5L8.74998 4.66667H12.25L4.37498 11.6667L5.24998 7.58333H2.33331Z"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              <div className="text-xs font-medium leading-normal text-white">
                Update
              </div>
            </GlowingBadge>

            <h1 className="mb-8 text-5xl font-semibold leading-[1.25] tracking-tight max-lg:text-[32px]">
              {selectedEntry?.heading ?? null}
            </h1>

            {selectedEntry?.video ? (
              <div className="mb-8">
                <iframe
                  className="aspect-video w-full rounded-[30px]"
                  src={selectedEntry.video}
                  title={selectedEntry.heading}
                  allowFullScreen
                ></iframe>
              </div>
            ) : null}

            <ContentWithStyles>
              {selectedEntry?.content ?? null}
            </ContentWithStyles>
          </div>
        </div>
      </div>
    </>
  );
}
